/**
 * Service JSON-LD for the EN and DE service pages (the TR pages carry their
 * own, byte-identical to the pre-redesign pages, in pad.js / screen.js /
 * assembly.js). Same shape as the TR schema, translated from it: no new
 * claims, no prices, the provider is the LocalBusiness in index.html.
 */

const PROVIDER = Object.freeze({
  '@type': 'LocalBusiness',
  '@id': 'https://ankaplastik.com/#organization',
  name: 'Ankaplastik',
})

/**
 * @param {{
 *   name: string,
 *   alternateName: string[],
 *   serviceType: string,
 *   description: string,
 *   catalog: string,
 *   items: string[],
 * }} fields
 * @returns {object}
 */
function serviceSchema({ name, alternateName, serviceType, description, catalog, items }) {
  return Object.freeze({
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    alternateName,
    provider: PROVIDER,
    serviceType,
    areaServed: { '@type': 'Country', name: 'Türkiye' },
    description,
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: catalog,
      itemListElement: items.map((item) => ({ '@type': 'Offer', itemOffered: { '@type': 'Service', name: item } })),
    },
  })
}

/** Keyed by service, then locale; schemaId matches the TR page's. */
export const SERVICE_SCHEMAS = Object.freeze({
  pad: {
    schemaId: 'tampon-baski',
    en: serviceSchema({
      name: 'Pad Printing Services',
      alternateName: ['Tampon Baskı', 'Tampondruck'],
      serviceType: 'Industrial Printing',
      description:
        'Pad printing for 3D and curved surfaces: precision printing on automotive interior trim parts, instrument panels and ISOFIX safety parts.',
      catalog: 'Pad Printing Applications',
      items: ['Automotive Interior Trim Printing', 'Instrument Panel Printing', 'ISOFIX Safety Part Printing', 'Multi-Color Printing'],
    }),
    de: serviceSchema({
      name: 'Tampondruck-Dienstleistungen',
      alternateName: ['Tampon Baskı', 'Pad Printing'],
      serviceType: 'Industriedruck',
      description:
        'Tampondruck, der sich 3D-Oberflächen anpasst: präzise Bedruckung von Fahrzeug-Innenraumteilen, Instrumententafeln und ISOFIX-Sicherheitsteilen.',
      catalog: 'Tampondruck-Anwendungen',
      items: [
        'Bedruckung von Fahrzeug-Innenraumteilen',
        'Bedruckung von Instrumententafeln',
        'Bedruckung von ISOFIX-Sicherheitsteilen',
        'Mehrfarbendruck',
      ],
    }),
  },
  screen: {
    schemaId: 'serigrafi',
    en: serviceSchema({
      name: 'Screen Printing Services',
      alternateName: ['Serigrafi Baskı', 'Siebdruck'],
      serviceType: 'Industrial Printing',
      description:
        'High-quality screen printing on large surfaces: panels for boilers and white goods, automotive instrument panels and industrial control panels.',
      catalog: 'Screen Printing Applications',
      items: [
        'Boiler and White Goods Panel Printing',
        'Automotive Instrument Panel Printing',
        'Industrial Control Panel Printing',
        'Glass and Metal Surface Printing',
      ],
    }),
    de: serviceSchema({
      name: 'Siebdruck-Dienstleistungen',
      alternateName: ['Serigrafi Baskı', 'Screen Printing'],
      serviceType: 'Industriedruck',
      description:
        'Hochwertiger Siebdruck auf großen Flächen: Blenden für Heiz- und Haushaltsgeräte, Kfz-Instrumententafeln und industrielle Bedienfelder.',
      catalog: 'Siebdruck-Anwendungen',
      items: [
        'Bedruckung von Heiz- und Haushaltsgeräteblenden',
        'Bedruckung von Kfz-Instrumententafeln',
        'Bedruckung industrieller Bedienfelder',
        'Bedruckung von Glas- und Metalloberflächen',
      ],
    }),
  },
  assembly: {
    schemaId: 'montaj-trim',
    en: serviceSchema({
      name: 'Assembly & Trim Services',
      alternateName: ['Montaj & Trim', 'Montage & Trim'],
      serviceType: 'Automotive Part Assembly',
      description:
        'Precision assembly of automotive interior trim parts and safety components: ISOFIX safety parts, clips and fasteners, decorative trim assembly.',
      catalog: 'Assembly Services',
      items: [
        'ISOFIX Safety Component Assembly',
        'Clip and Fastener Assembly',
        'Instrument Panel Sub-Assembly',
        'Door Panel Component Assembly',
      ],
    }),
    de: serviceSchema({
      name: 'Montage- und Trim-Dienstleistungen',
      alternateName: ['Montaj & Trim', 'Assembly & Trim'],
      serviceType: 'Montage von Fahrzeugteilen',
      description:
        'Präzise Montage von Fahrzeug-Innenraumteilen und Sicherheitskomponenten: ISOFIX-Sicherheitsteile, Clips und Befestigungselemente, Montage dekorativer Zierteile.',
      catalog: 'Montageleistungen',
      items: [
        'Montage von ISOFIX-Sicherheitskomponenten',
        'Montage von Clips und Befestigungselementen',
        'Montage von Instrumententafel-Baugruppen',
        'Montage von Türverkleidungskomponenten',
      ],
    }),
  },
})
